import has from 'lodash/has';
import get from 'lodash/get';
import { change, setSubmitFailed, stopSubmit } from 'redux-form';

import { formatQuery as encodeGetParams } from 'url-lib';
import { getSelectedPaymentMethod } from '../../selectors/payment';
import { track } from '@eventbrite/datalayer-library';
import { trackOrderFailure } from '../eventAnalytics';

import { getUserAccountDetails as getUserAccountDetailsApi } from '../../api/users';
import {
    INTERNAL_SERVER_ERROR,
    PAYMENT_PAY_WITH_VIRTUAL_INCENTIVES,
} from '../../containers/checkout/constants';

import { selectors as appSelectors } from '../../reducers/app';
import { saveSession } from '../../session';
import logger from '@eventbrite/client-logger';

import { getEventId } from '../../selectors/event';
import { getIsAppRebrandActive, isNativeApp } from '../../selectors/app';
import { shouldPerformTablessCheckout, redirectTo } from '../../utils/browser';
import {
    PAYMENT_METHODS,
    VIRTUAL_INCENTIVES_ERRORS,
    CHECKOUT_FORM_NAME,
    TICKETS_PAGE_FORM_NAME,
} from '../../constants';
import {
    setReloadAfterCheckoutAction,
    setWasExternalPaymentWindowBlocked,
} from '../app';
import {
    setCanRetrySubmitAction,
    setHasVirtualIncentivesErrorsAction,
    setHasRetryPaymentError,
} from './';
import { ERROR_EXPRESS_CHECKOUT_ORDER } from '../../constants/expressCheckout';
import { HeapEventName } from '../../constants/heap';
import { getOrderId } from '../../selectors/order/typedOrderSelectors';
import { CheckoutErrorName } from '@eventbrite/checkout-postmessage-api';
import { notifyParentOrderPlacingError } from '../../actions/notifyParent';

export const checkIfUserWasLoggedIn = () => async (dispatch, getState) => {
    const {
        order: { email = null, userId = null },
    } = getState();

    if (userId && email && email.length) {
        const { response } = await getUserAccountDetailsApi(email);

        if (
            response &&
            has(response, 'can_login') &&
            response.can_login === false
        ) {
            return dispatch(setReloadAfterCheckoutAction(true));
        }
    }

    return null;
};

export const userIsStoringNewCreditCard = (formData) =>
    formData.paymentMethod === PAYMENT_METHODS.CREDIT &&
    formData.payment.vaultId === PAYMENT_METHODS.CREDIT &&
    formData.payment.saveCreditCard;

export const getPostMessageActionParamsFromState = (state) => ({
    eventId: getEventId(state),
    widgetParentUrl: appSelectors.getWidgetParentUrl(state.app),
});

export const handleOrderError = (dispatch, error) => {
    if (error && error.errors) {
        /** Both JavaScript errors and server errors from the API will end up being caught here.
         *  However, this block is intended specifically for gracefully handling errors from the
         *  API and passing them to redux-form to be translated into user-friendly notifications.
         *  Any JavaScript errors that show up here should be fixed and should not be passed on.
         */
        dispatch(trackOrderFailure(error.errors._error));
        dispatch((_dispatch, getState) => {
            const state = getState();
            track({
                eventName: HeapEventName.orderFailure,
                eventData: {
                    label: error.errors._error,
                    selectedPaymentMethod: getSelectedPaymentMethod(state),
                    orderId: getOrderId(state),
                },
            });
        });

        const errorType = get(error, 'extraData.parsedError.error');
        const shouldStopSubmit = get(
            error,
            'extraData.parsedError.shouldStopSubmit',
        );
        const hasVirtualIncentiveErrors =
            VIRTUAL_INCENTIVES_ERRORS.includes(errorType);

        if (hasVirtualIncentiveErrors) {
            // After VI fails, we don't want to surface them again in the flow
            // setHasVirtualIncentiveErrors will be erased on resetOrder
            dispatch(setHasVirtualIncentivesErrorsAction(true));

            // Remove virtual incentive from form
            dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    PAYMENT_PAY_WITH_VIRTUAL_INCENTIVES,
                    false,
                ),
            );
        }

        if (errorType === 'RETRY_ERROR') {
            dispatch(setHasRetryPaymentError(true));
        }

        if (shouldStopSubmit) {
            dispatch(
                stopSubmit(CHECKOUT_FORM_NAME, {
                    _error: error.errors._error,
                }),
            );
        }

        if (INTERNAL_SERVER_ERROR === errorType || hasVirtualIncentiveErrors) {
            // allow the user to retry if they got a 500 error or their error is virtual incentive related
            dispatch(setCanRetrySubmitAction(true));
        } else {
            dispatch(setCanRetrySubmitAction(false));
        }
        throw error;
    }
};

export const handleOrderErrorInTicketSelectionPage = (dispatch) => {
    dispatch(
        notifyParentOrderPlacingError(
            CheckoutErrorName.ERROR_EXPRESS_CHECKOUT_ORDER,
        ),
    );
    dispatch(
        stopSubmit(TICKETS_PAGE_FORM_NAME, {
            _error: ERROR_EXPRESS_CHECKOUT_ORDER,
        }),
    );

    dispatch(setSubmitFailed(TICKETS_PAGE_FORM_NAME));
};

/**
 * Returns true if the user has chosen to use CREDIT payment method,
 * then chose a card stored in their account.
 *
 * Note: payment.vaultId === CREDIT if the user chose to add a new card.
 */
export const userSelectedStoredPaymentMethod = (formData) =>
    formData.paymentMethod === PAYMENT_METHODS.CREDIT &&
    formData.payment.vaultId &&
    formData.payment.vaultId !== PAYMENT_METHODS.CREDIT;

const _getBankName = (availablePaymentMethods, bankId) => {
    if (!availablePaymentMethods || !bankId) {
        return null;
    }

    const idealInstrument = availablePaymentMethods
        .find((method) => method.type === 'eventbrite')
        .instrumentTypes.find(
            (instrument) => instrument.type === PAYMENT_METHODS.IDEAL,
        );

    if (!idealInstrument) {
        return null;
    }

    const bankIdentifiers = idealInstrument.idealBanks;

    const bankIdentifier = bankIdentifiers.filter(
        (identifier) => identifier.bankId === bankId,
    );

    if (!bankIdentifier[0]) {
        return null;
    }

    return bankIdentifier[0].bankName;
};

export const redirectToExternalPayment = async (
    paymentMethod,
    getRedirectedPaymentOrderPromise,
    availablePaymentMethods,
    bankId,
    getState,
    dispatch,
) => {
    let url = `//${window.location.host}/checkout-widget-external-payment-redirect`;

    const appData = {};
    const performTabless =
        shouldPerformTablessCheckout() || isNativeApp(getState());

    appData.paymentMethod = paymentMethod;
    appData.bankName = _getBankName(availablePaymentMethods, bankId);
    appData.isAppRebrandActive = getIsAppRebrandActive(getState());

    const openExternalPaymentWindow = async () => {
        if (!window.open(url, 'external-payment-redirect')) {
            dispatch(setWasExternalPaymentWindowBlocked(true));
            await getRedirectedPaymentOrderPromise();
        }
    };

    // EB-95856 Some users cannot progress to order form on Embedded Checkout on some devices
    // iosWebView are removing window.opener data, so pass through GET parameters
    if (performTabless) {
        appData.redirectInstructions = await getRedirectedPaymentOrderPromise();
        url += `?${encodeGetParams({ appData: JSON.stringify(appData) })}`;
        const state = getState();
        saveSession(state);

        try {
            await redirectTo(url);
        } catch (error) {
            const orderId = getOrderId(state);
            const eventId = getEventId(state);
            logger.error(
                'Redirect to URL has failed',
                {
                    orderId,
                    eventId,
                    url,
                    error,
                },
                {
                    beforeSend: (report) => {
                        // eslint-disable-next-line no-param-reassign
                        report.groupingHash =
                            'redirecting-to-third-party-payment-on-tabless';
                    },
                },
            );
        }
    } else {
        appData._getRedirectedPaymentOrderPromise =
            getRedirectedPaymentOrderPromise;
        window.app = appData;

        await openExternalPaymentWindow();
    }
};
